import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart, MessageCircle, Share2, Bookmark, Flag, Eye, ChevronLeft, ChevronRight,
  Gauge, Wind, Fuel, Cog, Waves, CircleDot, Disc, Plane, Sparkles, Armchair, Cpu,
  Check, AlertTriangle, ShieldCheck, Users, Star, TrendingUp, TrendingDown,
  Zap, Timer, Weight, Trophy, Tag, ArrowRight, ChevronDown,
  Play, Award, Flame, Target, BarChart3, Calendar, ArrowUpRight,
  BookOpen, Layers, Wrench, Maximize2, X, Info, MapPin,
} from 'lucide-react';
import { build, overview, systems, breakdown, parts, specs, comments, moreBuilds, getListingsForPart, tunersBySystem } from './mockData';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './components/ui/dialog';

const iconMap = { Gauge, Wind, Fuel, Cog, Waves, CircleDot, Disc, Plane, Sparkles, Armchair, Cpu };
const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : n);

// System -> color theme + icon for product card visuals
const systemTheme = {
  engine:     { from: '#fef2f2', to: '#fecaca', ink: '#991b1b', icon: 'Gauge' },
  intake:     { from: '#eff6ff', to: '#bfdbfe', ink: '#1e40af', icon: 'Wind' },
  fuel:       { from: '#fff7ed', to: '#fed7aa', ink: '#9a3412', icon: 'Fuel' },
  drivetrain: { from: '#f5f3ff', to: '#ddd6fe', ink: '#5b21b6', icon: 'Cog' },
  suspension: { from: '#ecfeff', to: '#a5f3fc', ink: '#155e75', icon: 'Waves' },
  wheels:     { from: '#f8fafc', to: '#cbd5e1', ink: '#1e293b', icon: 'CircleDot' },
  brakes:     { from: '#fef2f2', to: '#fca5a5', ink: '#7f1d1d', icon: 'Disc' },
  aero:       { from: '#f0f9ff', to: '#7dd3fc', ink: '#0c4a6e', icon: 'Plane' },
  exterior:   { from: '#fafaf9', to: '#d6d3d1', ink: '#1c1917', icon: 'Sparkles' },
  interior:   { from: '#fdf4ff', to: '#f0abfc', ink: '#701a75', icon: 'Armchair' },
  tuning:     { from: '#ecfdf5', to: '#86efac', ink: '#14532d', icon: 'Cpu' },
};

// Clean product-card visual instead of random stock photos.
// Renders a branded placeholder with system icon + subtle detail.
const PartImage = ({ part, className = '' }) => {
  const theme = systemTheme[part.system] || systemTheme.engine;
  const Icon = iconMap[theme.icon] || Gauge;
  return (
    <div
      className={`relative w-full h-full overflow-hidden ${className}`}
      style={{ background: `radial-gradient(circle at 30% 25%, ${theme.from}, ${theme.to})` }}
    >
      {/* Blueprint grid */}
      <svg className="absolute inset-0 w-full h-full opacity-25" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id={`g-${part.id}`} width="24" height="24" patternUnits="userSpaceOnUse">
            <path d="M 24 0 L 0 0 0 24" fill="none" stroke={theme.ink} strokeWidth="0.4" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#g-${part.id})`} />
      </svg>

      {/* Diagonal shine */}
      <div className="absolute -left-1/3 -top-1/3 w-2/3 h-[180%] rotate-12 bg-white/30 blur-2xl pointer-events-none" />

      {/* Large centered icon */}
      <div className="absolute inset-0 flex items-center justify-center">
        <Icon size={96} strokeWidth={1.1} style={{ color: theme.ink, opacity: 0.85 }} />
      </div>

      {/* Bottom-left brand */}
      <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
        <div className="font-display font-bold uppercase text-[11px] tracking-[0.2em]" style={{ color: theme.ink, opacity: 0.75 }}>
          {part.brand}
        </div>
        <div className="font-mono text-[10px]" style={{ color: theme.ink, opacity: 0.55 }}>
          #{part.id.replace('p-', '').toUpperCase()}
        </div>
      </div>

      {/* Top-right dot pattern (sticker vibe) */}
      <div className="absolute top-3 right-3 flex gap-1">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: theme.ink, opacity: 0.4 }} />
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: theme.ink, opacity: 0.25 }} />
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: theme.ink, opacity: 0.1 }} />
      </div>
    </div>
  );
};

// ==================== TOP NAV ====================
// Imported from shared component so SHOP/EXPLORE/etc work as router links
// across both BuildShowcase and ProductPage.
import TopNav from './TopNav';

// ==================== HERO ====================
const Hero = () => {
  const [activeIdx, setActiveIdx] = useState(0);

  const next = () => setActiveIdx((i) => (i + 1) % build.gallery.length);
  const prev = () => setActiveIdx((i) => (i - 1 + build.gallery.length) % build.gallery.length);

  const current = build.gallery[activeIdx];
  const isVideo = current.type === 'video';

  return (
    <section className="relative">
      <div className="max-w-[1440px] mx-auto px-6 pt-8">
        <div className="relative w-full aspect-[16/9] rounded-3xl overflow-hidden bg-[var(--bg-subtle)] border border-[var(--border)]">
          {isVideo ? (
            <video
              key={current.src}
              poster={current.poster}
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
            >
              <source src={current.src} type="video/mp4" />
            </video>
          ) : (
            <div
              className="absolute inset-0 transition-[background-size,background-position] duration-500"
              style={{
                backgroundImage: `url(${current.src})`,
                backgroundSize: current.size || 'cover',
                backgroundPosition: current.position || 'center center',
                backgroundRepeat: 'no-repeat',
              }}
            />
          )}

          {/* Soft gradient scrim only at bottom for caption legibility */}
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />

          {/* Nav arrows */}
          <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/95 hover:bg-white flex items-center justify-center border border-[var(--border)] shadow transition">
            <ChevronLeft size={20} className="text-[var(--ink)]" />
          </button>
          <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/95 hover:bg-white flex items-center justify-center border border-[var(--border)] shadow transition">
            <ChevronRight size={20} className="text-[var(--ink)]" />
          </button>

          {/* Top-left badges */}
          <div className="absolute top-6 left-6 flex flex-col gap-2">
            <span className="chip chip-accent shadow-sm"><Award size={12}/> Featured Build</span>
            <span className="chip bg-white/95 shadow-sm"><Eye size={12}/> {fmt(build.stats.views)} views</span>
          </div>

          {/* Bottom caption */}
          <div className="absolute bottom-5 left-6 flex items-center gap-3 text-xs text-white">
            <span className="font-mono bg-black/50 backdrop-blur px-2 py-1 rounded">{activeIdx + 1} / {build.gallery.length}</span>
            <span className="font-mono bg-black/50 backdrop-blur px-2 py-1 rounded">{current.label}</span>
          </div>
        </div>

        {/* Thumbnail strip */}
        <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
          {build.gallery.map((g, i) => (
            <button key={`${g.type}-${g.label}`} className={`thumb relative ${i === activeIdx ? 'active' : ''}`} onClick={() => setActiveIdx(i)}>
              <div
                className="w-full h-full"
                style={{
                  backgroundImage: `url(${g.type === 'video' ? g.poster : g.src})`,
                  backgroundSize: g.size || 'cover',
                  backgroundPosition: g.position || 'center center',
                }}
              />
              {g.type === 'video' && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <span className="w-7 h-7 rounded-full bg-white/95 flex items-center justify-center">
                    <Play size={12} className="text-[var(--ink)] translate-x-[1px]" fill="currentColor" />
                  </span>
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== TITLE BLOCK ====================
const TitleBlock = () => (
  <section className="max-w-[1440px] mx-auto px-6 mt-10">
    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
      <div>
        <div className="section-label mb-4">
          {build.buildType}
        </div>
        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight text-[var(--ink)]">
          <span className="text-[var(--text-mute)]">{build.make.toUpperCase()}</span>{' '}
          <span>{build.title.toUpperCase()}</span>
        </h1>
        {build.buildName && (
          <div className="mt-2 font-display text-lg font-semibold tracking-[0.24em] text-[var(--accent)] uppercase">
            {build.buildName}
          </div>
        )}

        {/* Vehicle spec plate — Make / Model / Sub-Model / Year */}
        <div className="mt-6 inline-grid grid-cols-2 md:grid-cols-4 rounded-xl border border-[var(--border)] bg-[var(--bg-subtle)] overflow-hidden">
          {[
            ['Make',      build.make],
            ['Model',     build.title],
            ['Sub-Model', build.model],
            ['Year',      build.year],
          ].map(([k, v], idx) => (
            <div
              key={k}
              className={`px-5 py-3 ${idx > 0 ? 'md:border-l' : ''} ${idx >= 2 ? 'border-t md:border-t-0' : ''} ${idx === 1 ? 'border-l md:border-l' : ''} border-[var(--border)]`}
            >
              <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--text-mute)]">{k}</div>
              <div className="font-display text-base font-semibold text-[var(--ink)] leading-tight mt-0.5">{v}</div>
            </div>
          ))}
        </div>

        {/* Build summary — bold title + one-line description (Track Build pattern) */}
        {build.summary && (
          <p
            className="mt-5 text-[var(--ink)] leading-relaxed max-w-2xl"
            data-testid="build-summary"
          >
            {build.summaryTitle && (
              <span className="font-display font-bold text-[var(--ink)] mr-1">
                {build.summaryTitle} —
              </span>
            )}
            <span className="text-[var(--text-dim)]">{build.summary}</span>
          </p>
        )}

        {/* Best for — same inline style as the Touge Build summary */}
        <p className="mt-4 text-[var(--ink)] flex items-center gap-2 flex-wrap" data-testid="best-for-line">
          <Flame size={14} className="text-[var(--accent)]"/>
          <span className="font-display font-bold text-[var(--ink)]">Best for —</span>
          <span className="text-[var(--text-dim)]">{build.bestFor}</span>
        </p>

        {/* Hashtag tag row */}
        {build.tags?.length > 0 && (
          <div
            className="mt-3 flex items-center gap-2 flex-wrap"
            data-testid="build-tags"
          >
            {build.tags.map((t) => (
              <span
                key={t}
                className="font-mono text-xs text-[var(--text-dim)] hover:text-[var(--accent)] cursor-pointer transition"
              >
                #{t}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4 lg:items-end">
        <div className="flex items-center gap-3">
          <img src={build.owner.avatar} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-[var(--accent)]" />
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-semibold">{build.owner.name}</span>
              {build.owner.verified && <ShieldCheck size={14} className="text-[var(--blue)]" />}
            </div>
            <div className="text-xs text-[var(--text-mute)] font-mono">{build.owner.handle} · {fmt(build.owner.followers)} followers</div>
          </div>
          <button className="ml-2 btn-primary">Follow</button>
        </div>
        <div className="flex items-center gap-2">
          <ActionBtn icon={Heart} label={fmt(build.stats.likes)} />
          <ActionBtn icon={MessageCircle} label={fmt(build.stats.comments)} />
          <ActionBtn icon={Bookmark} label={fmt(build.stats.saves)} />
          <ActionBtn icon={Share2} label="Share" />
          <ActionBtn icon={Flag} />
        </div>
      </div>
    </div>
  </section>
);

const ActionBtn = ({ icon: Icon, label }) => (
  <button className="flex items-center gap-2 px-4 h-10 rounded-full border border-[var(--border)] bg-white hover:bg-[var(--bg-subtle)] hover:border-[var(--border-bright)] transition text-sm text-[var(--ink)]">
    <Icon size={15} />
    {label && <span className="font-mono text-xs">{label}</span>}
  </button>
);

// Rate-this-build CTA + modal — collects two distinct things:
//   • Quality rating (1–5 stars, qualitative: poor → excellent)
//   • Score (1–10 numeric, quantitative)
// MOCKED: submission only writes to local state + auto-closes.
const RateBuildButton = () => {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);   // 1-5 stars
  const [hoverRating, setHoverRating] = useState(0);
  const [score, setScore] = useState(0);     // 1-10
  const [submitted, setSubmitted] = useState(false);

  const ratingLabels = ['Poor', 'Fair', 'Good', 'Great', 'Excellent'];
  const ratingLabel = ratingLabels[(hoverRating || rating) - 1] || '';

  const reset = () => { setRating(0); setHoverRating(0); setScore(0); setSubmitted(false); };

  const submit = () => {
    if (rating < 1 || score < 1) return;
    setSubmitted(true);
    setTimeout(() => {
      setOpen(false);
      setTimeout(reset, 300);
    }, 1300);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="rate-build-btn"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--ink)] text-white text-xs font-medium hover:bg-black transition flex-shrink-0"
      >
        <Star size={12} fill="currentColor" className="text-[var(--star)]" />
        Rate this build
      </button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="sm:max-w-md" data-testid="rate-build-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Rate this build</DialogTitle>
            <DialogDescription>
              Share your take on {build.buildName}.
            </DialogDescription>
          </DialogHeader>

          {submitted ? (
            <div className="py-8 text-center" data-testid="rate-build-thanks">
              <div className="w-14 h-14 mx-auto rounded-full bg-[var(--green-soft)] flex items-center justify-center text-[var(--green)]">
                <Check size={26} />
              </div>
              <div className="mt-4 font-display text-lg font-semibold text-[var(--ink)]">
                Thanks for rating!
              </div>
              <div className="text-sm text-[var(--text-dim)] mt-1">
                Quality {rating}/5 · Score {score}/10
              </div>
            </div>
          ) : (
            <div className="py-2 space-y-6">
              {/* Quality rating — 1-5 stars */}
              <div>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-display tracking-widest uppercase text-[var(--text-mute)] font-semibold">
                    Quality rating
                  </div>
                  <span className="text-xs font-mono text-[var(--text-dim)]">
                    {ratingLabel || 'Tap a star'}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-2 select-none">
                  {[1, 2, 3, 4, 5].map((v) => {
                    const active = (hoverRating || rating) >= v;
                    return (
                      <button
                        key={v}
                        type="button"
                        onMouseEnter={() => setHoverRating(v)}
                        onMouseLeave={() => setHoverRating(0)}
                        onClick={() => setRating(v)}
                        data-testid={`rate-star-${v}`}
                        className="p-1 transition-transform hover:scale-110"
                        aria-label={`${v} of 5`}
                      >
                        <Star
                          size={32}
                          className={active ? 'text-[var(--star)]' : 'text-[var(--border-bright)]'}
                          fill={active ? 'currentColor' : 'none'}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Score — 1-10 numeric chips */}
              <div>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-display tracking-widest uppercase text-[var(--text-mute)] font-semibold">
                    Build score
                  </div>
                  <span className="text-xs font-mono text-[var(--text-dim)]">
                    {score ? `${score} / 10` : 'Pick a number'}
                  </span>
                </div>
                <div className="grid grid-cols-10 gap-1.5 mt-2">
                  {Array.from({ length: 10 }).map((_, i) => {
                    const v = i + 1;
                    const active = score === v;
                    return (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setScore(v)}
                        data-testid={`rate-score-${v}`}
                        className={`h-10 rounded-md text-sm font-mono font-semibold transition border ${
                          active
                            ? 'bg-[var(--accent)] text-white border-[var(--accent)]'
                            : 'bg-white text-[var(--ink)] border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)]'
                        }`}
                      >
                        {v}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {!submitted && (
            <DialogFooter>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-4 py-2 rounded-lg border border-[var(--border)] text-sm hover:bg-[var(--bg-subtle)] transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={rating < 1 || score < 1}
                data-testid="rate-build-submit"
                className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Submit rating <ArrowRight size={14} />
              </button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// ==================== KPI STRIP ====================
// Build Score sits in its own hero card on the left; the 4 performance KPIs
// share the right-hand area in a 2x2 / 1x4 grid depending on viewport.
const KPIStrip = () => {
  const kpiIcon = { gauge: Gauge, torque: Zap, stopwatch: Timer, weight: Weight, speed: Flame, award: Award };

  // Score → green; +N → green when not inverse; -N → green only if inverse.
  const isPositive = (k) => {
    if (k.kind === 'score') return true;
    return k.inverse ? k.delta.startsWith('-') : k.delta.startsWith('+');
  };

  const DeltaIcon = ({ k, positive }) => {
    if (k.kind === 'score') return <Trophy size={11} className="inline mr-0.5" />;
    if (positive) return <TrendingUp size={11} className="inline mr-0.5" />;
    return <TrendingDown size={11} className="inline mr-0.5" />;
  };

  const score = build.kpis.find((k) => k.kind === 'score');
  const perf = build.kpis.filter((k) => k.kind !== 'score');

  return (
    <section className="max-w-[1440px] mx-auto px-6 mt-10">
      <div className="grid lg:grid-cols-[1fr_2.4fr] gap-4">
        {/* Build Score — distinct hero card */}
        {score && (
          <div
            data-testid="build-score-card"
            className="relative overflow-hidden rounded-2xl p-6 border border-[var(--border)]"
            style={{
              backgroundColor: '#f6f4f0',
              backgroundImage:
                "radial-gradient(900px 400px at -10% -30%, rgba(138,15,15,0.06), transparent 55%)," +
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'><g fill='none' stroke='%23000' stroke-opacity='0.04' stroke-width='1'><circle cx='80' cy='80' r='70'/><circle cx='80' cy='80' r='52'/><circle cx='80' cy='80' r='34'/><path d='M0 80 H160 M80 0 V160' stroke-dasharray='4 6'/></g></svg>\")",
              backgroundRepeat: 'no-repeat, repeat',
              backgroundPosition: 'center, center',
            }}
          >
            <div className="relative">
              {/* Top row: label + Rate this build CTA */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 text-[12px] font-display tracking-[0.22em] uppercase text-[var(--ink)] font-bold">
                  <span className="w-6 h-px bg-[var(--accent)]" />
                  <Award size={14} className="text-[var(--accent)]" />
                  Build Score
                </div>
                <RateBuildButton />
              </div>

              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-display text-6xl font-bold tracking-tight text-[var(--ink)]">
                  {score.value}
                </span>
                <span className="font-mono text-base text-[var(--text-dim)]">{score.unit}</span>
              </div>
              <div className="mt-1 flex items-center gap-1 text-[var(--star)]">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={16} fill="currentColor" />
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                <span className="font-mono text-[13px] text-[var(--ink)]/70 font-medium">
                  {score.subLabel}
                </span>
                <span className="font-mono text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 bg-[var(--accent)] text-white">
                  <Trophy size={11} className="inline mr-0.5" />
                  {score.delta}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Performance KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {perf.map((k) => {
            const Icon = kpiIcon[k.icon] || Gauge;
            const positive = isPositive(k);
            return (
              <div key={k.label} className="card card-hover p-5 relative overflow-hidden">
                <div className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-[var(--accent-soft)]" />
                <div className="relative">
                  <div className="section-label">
                    <Icon size={13} /> {k.label}
                  </div>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="font-display text-4xl font-bold text-[var(--ink)]">{k.value}</span>
                    <span className="font-mono text-sm text-[var(--text-dim)]">{k.unit}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="font-mono text-[11px] text-[var(--text-mute)] truncate">
                      stock: {k.stock}
                    </span>
                    <span className={`font-mono text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${positive ? 'bg-[var(--green-soft)] text-[var(--green)]' : 'bg-[var(--accent-soft)] text-[var(--accent)]'}`}>
                      <DeltaIcon k={k} positive={positive} />
                      {k.delta}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ==================== TAB BAR ====================
const TABS = [
  { id: 'Overview',        sub: 'story',            icon: BookOpen },
  { id: 'Breakdown',       sub: 'how it performs',  icon: Layers },
  { id: 'Parts',           sub: "what's used",      icon: Wrench },
  { id: 'Specs',           sub: 'results',          icon: Gauge },
  { id: 'Tagged Products', sub: 'shop the look',    icon: Tag },
];
const TabBar = ({ active, setActive }) => (
  <div className="sticky top-16 z-30 bg-white/90 backdrop-blur border-b border-[var(--border)] mt-10">
    <div className="max-w-[1440px] mx-auto px-6 flex items-center gap-8 md:gap-12 overflow-x-auto tab-strip">
      {TABS.map(({ id, sub, icon: Icon }) => (
        <button
          key={id}
          className={`tab-btn flex items-start gap-2 !py-4 ${active === id ? 'active' : ''}`}
          onClick={() => setActive(id)}
        >
          <Icon size={16} strokeWidth={active === id ? 2.4 : 1.8} className="mt-0.5" />
          <span className="flex flex-col items-start leading-tight">
            <span>{id}</span>
            <span className="font-mono text-[10px] tracking-[0.15em] normal-case text-[var(--text-mute)] mt-0.5">
              ({sub})
            </span>
          </span>
        </button>
      ))}
    </div>
  </div>
);

// ==================== OVERVIEW ====================
const OverviewTab = ({ onGoToBreakdown }) => {
  // Compute the Build Investment numbers from the same parts catalog used
  // everywhere else, so this stays in sync with Breakdown / Parts tabs.
  const totalParts = parts.reduce((sum, p) => sum + p.priceNum, 0);
  const totalLabour = parts.reduce((sum, p) => sum + (p.pricePaidNum - p.priceNum), 0);
  const totalCost = totalParts + totalLabour;
  const productCount = parts.length;

  // De-duplicate tuners across systems (same shop may appear twice).
  const tunerSet = new Map();
  Object.values(tunersBySystem).forEach((arr) => {
    arr.forEach((t) => { if (!tunerSet.has(t.name)) tunerSet.set(t.name, t); });
  });
  const tuners = Array.from(tunerSet.values());

  return (
    <div className="grid lg:grid-cols-3 gap-8 mt-10">
      <div className="lg:col-span-2 space-y-6">
        {/* Build Description */}
        <div className="card p-8">
          <div className="section-label mb-4">Build Description</div>
          <p className="text-lg leading-relaxed text-[var(--ink)]">{overview.description}</p>
        </div>

        {/* Owner Insights */}
        <div className="card p-8 bg-gradient-to-br from-white to-[var(--accent-soft)]">
          <div className="section-label mb-4"><Trophy size={13}/> Owner Insights</div>
          <p className="text-lg leading-relaxed text-[var(--ink)]">{overview.ownerInsights}</p>
        </div>

        {/* Build Goals */}
        <div className="card p-8">
          <div className="section-label mb-4"><Target size={13}/> Build Goals</div>
          <ul className="space-y-3">
            {overview.goals.map((g) => (
              <li key={g} className="flex items-start gap-3 text-[var(--ink)]">
                <Check size={18} className="text-[var(--accent)] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                <span className="text-base">{g}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Sidebar — build investment + build timeline */}
      <aside className="space-y-6">
        {/* Build Investment — replaces Quick Facts */}
        <div className="card p-6" data-testid="build-investment">
          <div className="section-label mb-5"><BarChart3 size={13}/> Build Investment</div>

          {/* Total cost — primary stat, bold */}
          <div className="pb-4 border-b border-[var(--border)]">
            <div className="text-[10px] font-display tracking-widest uppercase text-[var(--text-mute)]">
              Total build cost
            </div>
            <div className="font-display text-3xl font-bold text-[var(--accent)] mt-1 font-mono">
              ${fmt(totalCost)}
            </div>
          </div>

          {/* Stat rows */}
          <div className="space-y-3 text-sm pt-4">
            <div className="flex justify-between items-center pb-3 border-b border-[var(--border)]">
              <span className="text-[var(--text-dim)]">Parts cost</span>
              <span className="font-mono text-xs text-[var(--ink)]">${fmt(totalParts)}</span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b border-[var(--border)]">
              <span className="text-[var(--text-dim)]">Labour cost</span>
              <span className="font-mono text-xs text-[var(--ink)]">${fmt(totalLabour)}</span>
            </div>
            <div className="flex justify-between items-center pb-3 border-b border-[var(--border)]">
              <span className="text-[var(--text-dim)]">Modifications</span>
              <span className="font-mono text-xs text-[var(--ink)]">{productCount} parts across {systems.length} systems</span>
            </div>

            {/* Tuners — name + initials avatar */}
            <div className="pb-1">
              <span className="text-[var(--text-dim)] block mb-2">Tuners on this build</span>
              <div className="flex flex-wrap gap-2">
                {tuners.slice(0, 6).map((t) => (
                  <div
                    key={t.name}
                    data-testid={`overview-tuner-${t.name.replace(/\s+/g, '-').toLowerCase()}`}
                    className="flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full border border-[var(--border)] bg-white"
                  >
                    {t.logo ? (
                      <img src={t.logo} alt={t.name} className="w-5 h-5 rounded-full object-cover border border-[var(--border)]" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-[var(--ink)] text-white font-display font-bold text-[8px] flex items-center justify-center flex-shrink-0">
                        {t.name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                      </div>
                    )}
                    <span className="text-xs text-[var(--ink)] font-medium">{t.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Detailed system-level cost CTA — jumps to Breakdown tab */}
          <button
            onClick={onGoToBreakdown}
            data-testid="overview-system-cost-btn"
            className="w-full mt-5 btn-primary justify-center"
          >
            Detailed system-level cost <ArrowRight size={14} />
          </button>
        </div>

        <div className="card p-6">
          <div className="section-label mb-5"><Calendar size={13}/> Build timeline</div>
          <div className="relative pl-6 space-y-5">
            <div className="absolute left-[7px] top-2 bottom-2 w-[2px] bg-[var(--border)]" />
            {overview.timeline.map((t, i) => (
              <div key={`${t.month}-${t.label}`} className="relative">
                <div className={`absolute -left-6 top-1.5 w-4 h-4 rounded-full border-2 ${i === overview.timeline.length - 1 ? 'bg-[var(--accent)] border-[var(--accent)]' : 'bg-white border-[var(--border-bright)]'}`} />
                <div className="font-mono text-xs text-[var(--text-mute)]">{t.month}</div>
                <div className="text-sm text-[var(--ink)]">{t.label}</div>
              </div>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
};

// ==================== BREAKDOWN ====================
const BreakdownTab = ({ onJumpToPart }) => {
  const [activeSys, setActiveSys] = useState('engine');
  const sys = systems.find((s) => s.id === activeSys);
  const b = breakdown[activeSys];
  const Icon = iconMap[sys.icon];
  const sysParts = parts.filter((p) => p.system === activeSys);
  const sysPartsCost = sysParts.reduce((s, p) => s + p.priceNum, 0);
  const sysLabourCost = sysParts.reduce((s, p) => s + (p.pricePaidNum - p.priceNum), 0);

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-8 mt-10">
      <aside>
        <div className="section-label mb-4">Systems · 11</div>
        <div className="space-y-1">
          {systems.map((s) => {
            const SIcon = iconMap[s.icon];
            const count = parts.filter((p) => p.system === s.id).length;
            return (
              <div key={s.id} className={`sys-item ${activeSys === s.id ? 'active' : ''}`} onClick={() => setActiveSys(s.id)}>
                <div className="sys-icon-wrap"><SIcon size={18} /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{s.name}</div>
                  <div className="text-[11px] font-mono text-[var(--text-mute)] flex items-center gap-1">
                    {count} parts ·
                    <Star size={10} className="text-[var(--star)]" fill="currentColor" />
                    {s.rating}
                  </div>
                </div>
                <ChevronRight size={14} className="text-[var(--text-mute)]" />
              </div>
            );
          })}
        </div>
      </aside>

      <div className="space-y-5">
        {/* System header */}
        <div className="card p-8">
          <div className="flex items-start gap-5">
            <div className="w-16 h-16 rounded-2xl bg-[var(--accent-soft)] border border-[var(--accent)]/30 flex items-center justify-center text-[var(--accent)]">
              <Icon size={28} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-display text-3xl font-bold text-[var(--ink)]">{sys.name}</h2>
                <span className={`chip font-display tracking-widest uppercase text-[10px] trust-${b.trust}`}>
                  <ShieldCheck size={11} /> {b.trust}
                </span>
              </div>
              <p className="mt-3 text-[var(--text-dim)] leading-relaxed">{b.summary}</p>
              <div className="mt-4 flex items-center gap-5 text-sm flex-wrap">
                <span className="flex items-center gap-1.5">
                  <Star size={14} className="text-[var(--star)]" fill="currentColor" />
                  <span className="font-mono font-semibold">{b.rating}</span>
                  <span className="text-[var(--text-mute)]"> / 5</span>
                </span>
                <span className="flex items-center gap-1.5 text-[var(--text-dim)]">
                  <Trophy size={14}/>
                  <span className="font-mono">{Math.floor(b.buildersUsing * 0.87)}</span>
                  recommend
                </span>
              </div>
            </div>
          </div>

          {/* Total estimated system cost — same component as Parts tab */}
          {sysParts.length > 0 && (
            <div className="mt-6 pt-6 border-t border-[var(--border)]" data-testid={`breakdown-cost-${activeSys}`}>
              <div className="section-label mb-4">
                <BarChart3 size={13} /> Total estimated system cost
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="rounded-lg border border-[var(--border)] bg-white px-4 py-3">
                  <div className="text-[10px] font-display tracking-widest uppercase text-[var(--text-mute)]">Parts</div>
                  <div className="font-mono text-xl font-semibold text-[var(--ink)] mt-1">${fmt(sysPartsCost)}</div>
                </div>
                <div className="rounded-lg border border-[var(--border)] bg-white px-4 py-3">
                  <div className="text-[10px] font-display tracking-widest uppercase text-[var(--text-mute)]">Labour</div>
                  <div className="font-mono text-xl font-semibold text-[var(--ink)] mt-1">${fmt(sysLabourCost)}</div>
                </div>
                <div className="rounded-lg border border-[var(--accent)]/40 bg-[var(--accent-soft)] px-4 py-3">
                  <div className="text-[10px] font-display tracking-widest uppercase text-[var(--accent)]">Total</div>
                  <div className="font-mono text-xl font-semibold text-[var(--accent)] mt-1">${fmt(sysPartsCost + sysLabourCost)}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Parts in this system — clickable, jumps to Parts tab */}
        {sysParts.length > 0 && (
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="section-label"><Wrench size={13}/> Parts in this system</div>
              <span className="font-mono text-xs text-[var(--text-mute)]">{sysParts.length} {sysParts.length === 1 ? 'part' : 'parts'}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {sysParts.map((p) => (
                <button
                  key={p.id}
                  onClick={() => onJumpToPart && onJumpToPart(p.id)}
                  data-testid={`breakdown-part-${p.id}`}
                  className="group flex items-center gap-3 p-3 rounded-xl border border-[var(--border)] hover:border-[var(--accent)] hover:bg-[var(--accent-soft)] bg-white transition text-left"
                >
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-[var(--bg-subtle)] flex-shrink-0 border border-[var(--border)]">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-display tracking-widest uppercase text-[var(--text-mute)]">{p.brand}</div>
                    <div className="text-sm font-semibold text-[var(--ink)] truncate">{p.name}</div>
                    <div className="text-[11px] font-mono text-[var(--text-mute)] mt-0.5">
                      <Star size={9} className="inline text-[var(--star)] mr-0.5" fill="currentColor" />
                      {p.rating} · MSRP ${fmt(p.priceNum)}
                    </div>
                  </div>
                  <ArrowUpRight size={15} className="text-[var(--text-mute)] group-hover:text-[var(--accent)] flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Pros / Cons */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="card p-6 bg-[var(--green-soft)]/30">
            <div className="section-label mb-4"><Check size={13} className="text-[var(--green)]" /> What works</div>
            <ul className="space-y-2.5">
              {b.pros.map((p) => (
                <li key={p} className="flex gap-3 text-sm">
                  <Check size={16} className="text-[var(--green)] flex-shrink-0 mt-0.5" />
                  <span className="text-[var(--ink)]">{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="card p-6 bg-[var(--amber-soft)]/30">
            <div className="section-label mb-4"><AlertTriangle size={13} className="text-[var(--amber)]" /> Trade-offs</div>
            <ul className="space-y-2.5">
              {b.cons.map((p) => (
                <li key={p} className="flex gap-3 text-sm">
                  <AlertTriangle size={16} className="text-[var(--amber)] flex-shrink-0 mt-0.5" />
                  <span className="text-[var(--ink)]">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Insight accordions — body is rendered as bullet points */}
        {[
          ['Performance impact', b.sections.performanceImpact, TrendingUp, true],
          ['Driving experience', b.sections.drivingExperience, Gauge, true],
          ['Reliability', b.sections.reliability, ShieldCheck, false],
          ['Install & setup', b.sections.installSetup, Cog, false],
          ['Things to know', b.sections.thingsToKnow, AlertTriangle, false],
          ['Comparison vs alternatives', b.sections.comparison, BarChart3, false],
          ['Recommendations', b.sections.recommendations, Trophy, false],
        ].map(([title, body, Ic, openDefault]) => (
          <InsightRow key={title} title={title} body={body} Icon={Ic} defaultOpen={openDefault} />
        ))}
      </div>
    </div>
  );
};

// Split a sentence into separate bullets on every full stop (ignoring periods
// inside numbers like 4.7 or 0.8).
const splitToBullets = (text) =>
  String(text)
    .split(/(?<=\D)\.\s+(?=[A-Z0-9])/)
    .map((s) => s.trim().replace(/\.$/, ''))
    .filter(Boolean);

const InsightRow = ({ title, body, Icon, defaultOpen }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  // Accept either an explicit array of bullets, or a string we split on
  // sentence boundaries.
  const items = Array.isArray(body) ? body : splitToBullets(body);
  return (
    <div className="card">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-6 text-left">
        <div className="flex items-center gap-3">
          <Icon size={18} className="text-[var(--accent)]" />
          <span className="font-display text-base font-semibold tracking-wide uppercase text-[var(--ink)]">{title}</span>
        </div>
        <ChevronDown size={18} className={`transition-transform ${open ? 'rotate-180' : ''} text-[var(--text-dim)]`} />
      </button>
      {open && items.length > 0 && (
        <ul className="px-6 pb-6 -mt-2 space-y-2 fade-up">
          {items.map((it) => (
            <li key={it} className="flex gap-3 text-[var(--text-dim)] leading-relaxed">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--accent)] flex-shrink-0" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ==================== PARTS ====================
// Layout mirrors the Breakdown tab: systems sidebar on the left,
// master-detail product selector on the right.
// Flow: System → list of products in that system → full product info.
const PartsTab = ({ initialPartId, onConsumed }) => {
  // If we were navigated here with a specific partId (e.g. from Breakdown),
  // start on that part's system; otherwise default to engine.
  const initialPart = initialPartId ? parts.find((p) => p.id === initialPartId) : null;
  const [activeSys, setActiveSys] = useState(initialPart?.system || 'engine');
  const [activePartId, setActivePartId] = useState(
    initialPart?.id || parts.find((p) => p.system === 'engine')?.id
  );

  // Honour later prop changes (e.g. user clicks another part in Breakdown,
  // jumps back, then clicks a different part).
  useEffect(() => {
    if (!initialPartId) return;
    const p = parts.find((pp) => pp.id === initialPartId);
    if (!p) return;
    setActiveSys(p.system);
    setActivePartId(p.id);
    if (onConsumed) onConsumed();
  }, [initialPartId, onConsumed]);

  const systemParts = useMemo(
    () => parts.filter((p) => p.system === activeSys),
    [activeSys]
  );

  // When switching systems, jump to that system's first part.
  const selectSystem = (id) => {
    setActiveSys(id);
    const first = parts.find((p) => p.system === id);
    if (first) setActivePartId(first.id);
  };

  const sys = systems.find((s) => s.id === activeSys);
  const SysIcon = iconMap[sys.icon];

  const activePart = systemParts.find((p) => p.id === activePartId) || systemParts[0];

  // Cost breakdown for this system: parts $ value, labour $ value.
  const partsCost  = systemParts.reduce((s, p) => s + p.priceNum, 0);
  const labourCost = systemParts.reduce((s, p) => s + (p.pricePaidNum - p.priceNum), 0);

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-8 mt-10" data-testid="parts-tab">
      {/* Left: systems sidebar — identical styling to Breakdown */}
      <aside>
        <div className="section-label mb-4">Systems · {systems.length}</div>
        <div className="space-y-1">
          {systems.map((s) => {
            const SIcon = iconMap[s.icon];
            const count = parts.filter((p) => p.system === s.id).length;
            return (
              <div
                key={s.id}
                data-testid={`parts-system-${s.id}`}
                className={`sys-item ${activeSys === s.id ? 'active' : ''}`}
                onClick={() => selectSystem(s.id)}
              >
                <div className="sys-icon-wrap"><SIcon size={18} /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{s.name}</div>
                  <div className="text-[11px] font-mono text-[var(--text-mute)] flex items-center gap-1">
                    {count} parts ·
                    <Star size={10} className="text-[var(--star)]" fill="currentColor" />
                    {s.rating}
                  </div>
                </div>
                <ChevronRight size={14} className="text-[var(--text-mute)]" />
              </div>
            );
          })}
        </div>
      </aside>

      {/* Right: system header + part picker + full product detail */}
      <div className="space-y-5">
        {/* System header */}
        <div className="card p-8">
          <div className="flex items-start gap-5">
            <div className="w-16 h-16 rounded-2xl bg-[var(--accent-soft)] border border-[var(--accent)]/30 flex items-center justify-center text-[var(--accent)]">
              <SysIcon size={28} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-display text-3xl font-bold text-[var(--ink)]">{sys.name}</h2>
                <span className="chip font-display tracking-widest uppercase text-[10px]">
                  <Wrench size={11} /> {systemParts.length} parts
                </span>
              </div>
              <p className="mt-3 text-[var(--text-dim)] leading-relaxed">
                {breakdown[activeSys]?.summary}
              </p>
            </div>
          </div>

          {/* Parts picker — moved INSIDE the system card, between
              system summary and Tune by */}
          {systemParts.length > 0 ? (
            <div className="mt-6 pt-6 border-t border-[var(--border)]">
              <div className="section-label mb-3"><Wrench size={13} /> Parts in this system</div>
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
                {systemParts.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setActivePartId(p.id)}
                    data-testid={`parts-product-chip-${p.id}`}
                    className={`part-chip ${activePartId === p.id ? 'active' : ''}`}
                  >
                    <div className="part-chip-thumb">
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="text-left min-w-0">
                      <div className="text-[10px] font-display uppercase tracking-widest text-[var(--text-mute)] truncate">{p.brand}</div>
                      <div className="text-sm font-semibold text-[var(--ink)] truncate">{p.name}</div>
                      <div className="flex items-center gap-1.5 text-[11px] font-mono">
                        <Star size={10} className="text-[var(--star)]" fill="currentColor" />
                        <span className="text-[var(--ink)]">{p.rating}</span>
                        <span className="text-[var(--text-mute)]">· MSRP ${fmt(p.priceNum)}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-6 pt-6 border-t border-[var(--border)] text-center">
              <div className="w-12 h-12 mx-auto rounded-2xl bg-[var(--bg-subtle)] flex items-center justify-center text-[var(--text-mute)]">
                <Wrench size={20} />
              </div>
              <h4 className="mt-3 font-display text-base font-semibold text-[var(--ink)]">No products tagged yet for this system</h4>
              <p className="mt-1 text-sm text-[var(--text-dim)]">
                Stan has covered this system in the Breakdown tab but hasn&apos;t linked the individual products yet.
              </p>
            </div>
          )}
        </div>

        {/* Selected product detail */}
        {activePart && <ProductDetail part={activePart} />}
      </div>
    </div>
  );
};

// Full product-detail card — info-only.
// Image lives in the parts picker chip above; pricing + listings live in
// the Tagged Products tab.
const ProductDetail = ({ part }) => {
  const sysBreakdown = breakdown[part.system] || {};
  // Per-part overrides preferred; otherwise fall back to system-level
  // breakdown content (pros / cons + section text).
  const whatWorks = part.whatWorks || sysBreakdown.pros || [];
  const tradeOffs = part.tradeOffs || sysBreakdown.cons || [];
  const drivingExperience = part.drivingExperience || sysBreakdown.sections?.drivingExperience;
  const reliability = part.reliability || sysBreakdown.sections?.reliability;
  const bestFor = part.bestFor || [`${build.buildType} drivers running a ${part.system} upgrade`];

  // Stats row — "Used in X builds | Y% recommended"
  const usedInBuilds = part.usedInBuilds ?? part.reviews ?? 0;
  const recommendPercent =
    part.recommendPercent ?? Math.round(((part.rating || 0) / 5) * 100);

  return (
    <article
      key={part.id}
      data-testid={`product-detail-${part.id}`}
      className="card overflow-hidden fade-up"
    >
      {/* Heading + builder's note */}
      <div className="p-7">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] font-display tracking-widest uppercase text-[var(--text-mute)]">{part.brand}</div>
            <h3 className="font-display text-2xl font-bold text-[var(--ink)] mt-1 leading-tight">{part.name}</h3>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {part.featured && (
              <span className="chip bg-[var(--accent)] text-white border-[var(--accent)]">
                <Flame size={11} /> Featured
              </span>
            )}
            <span className="chip">
              <Star size={11} className="text-[var(--star)]" fill="currentColor" />
              <span className="font-mono">{part.rating}</span>
              <span className="text-[var(--text-mute)] font-mono">· {part.reviews}</span>
            </span>
          </div>
        </div>

        <div className="mt-5 p-4 rounded-lg bg-[var(--amber-soft)] border border-[var(--amber)]/25">
          <div className="flex items-center gap-1.5 text-[10px] font-display tracking-widest uppercase text-[var(--amber)] mb-1.5">
            <Trophy size={11} /> Builder&apos;s note
          </div>
          <p className="leading-relaxed text-[var(--text-dim)] text-sm">{part.note}</p>
        </div>
      </div>

      {/* Why this part */}
      {part.whyThisPart && (
        <div className="px-7 pt-2 pb-1 border-t border-[var(--border)]">
          <div className="section-label mb-3 mt-6"><Target size={13} className="text-[var(--accent)]" /> Why this part</div>
          <p className="text-[var(--text-dim)] leading-relaxed">{part.whyThisPart}</p>
        </div>
      )}

      {/* Used-in / Recommended stat row + What works / Trade-offs */}
      <div className="px-7 py-6 border-t border-[var(--border)]">
        <div className="flex items-center gap-3 flex-wrap mb-5" data-testid="part-trust-row">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--border)] bg-white text-sm">
            <Users size={13} className="text-[var(--accent)]" />
            <span className="text-[var(--text-dim)]">Used in</span>
            <span className="font-mono font-semibold text-[var(--ink)]">{fmt(usedInBuilds)}</span>
            <span className="text-[var(--text-dim)]">builds</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--green)]/30 bg-[var(--green-soft)] text-sm">
            <Trophy size={13} className="text-[var(--green)]" />
            <span className="font-mono font-semibold text-[var(--green)]">{recommendPercent}%</span>
            <span className="text-[var(--green)]">Recommended</span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="card p-5 bg-[var(--green-soft)]/30">
            <div className="section-label mb-3"><Check size={13} className="text-[var(--green)]" /> What works</div>
            <ul className="space-y-2">
              {whatWorks.map((w) => (
                <li key={w} className="flex gap-2 text-sm text-[var(--ink)] leading-relaxed">
                  <Check size={15} className="text-[var(--green)] flex-shrink-0 mt-0.5" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="card p-5 bg-[var(--amber-soft)]/30">
            <div className="section-label mb-3"><AlertTriangle size={13} className="text-[var(--amber)]" /> Trade-offs</div>
            <ul className="space-y-2">
              {tradeOffs.map((t) => (
                <li key={t} className="flex gap-2 text-sm text-[var(--ink)] leading-relaxed">
                  <AlertTriangle size={15} className="text-[var(--amber)] flex-shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Insight accordions — bullet points for each topic */}
      <div className="px-7 pb-7 space-y-3">
        {part.performance && (
          <InsightRow title="Performance impact" body={part.performance} Icon={TrendingUp} defaultOpen />
        )}
        {drivingExperience && (
          <InsightRow title="Driving experience" body={drivingExperience} Icon={Gauge} />
        )}
        {reliability && (
          <InsightRow title="Reliability" body={reliability} Icon={ShieldCheck} />
        )}
        {part.install && (
          <InsightRow title="Install & setup" body={part.install} Icon={Cog} />
        )}
        {part.thingsToKnow && (
          <InsightRow title="Things to know" body={part.thingsToKnow} Icon={AlertTriangle} />
        )}
        {bestFor && bestFor.length > 0 && (
          <InsightRow title="Best for" body={bestFor} Icon={Target} />
        )}
      </div>
    </article>
  );
};

// ==================== SPECS ====================
const SpecsTab = () => (
  <div className="mt-10 space-y-8">
    <div>
      <h2 className="font-display text-3xl font-bold text-[var(--ink)] mb-1">Results · Stock vs Build</h2>
      <p className="text-sm text-[var(--text-dim)]">Every number measured, verified, and compared.</p>
    </div>

    <div className="grid md:grid-cols-2 gap-4">
      {specs.performance.map((s) => {
        const stockPct = (s.stock / s.max) * 100;
        const builtPct = (s.built / s.max) * 100;
        const delta = s.inverse ? s.stock - s.built : s.built - s.stock;
        const deltaPct = s.inverse ? (((s.stock - s.built) / s.stock) * 100) : (((s.built - s.stock) / s.stock) * 100);
        const positive = s.inverse ? s.built < s.stock : s.built > s.stock;
        return (
          <div key={s.label} className="card p-5">
            <div className="flex items-center justify-between">
              <span className="font-display font-semibold tracking-wide text-[var(--ink)]">{s.label}</span>
              <span className={`font-mono text-xs px-2 py-0.5 rounded-full font-semibold ${positive ? 'bg-[var(--green-soft)] text-[var(--green)]' : 'bg-[var(--accent-soft)] text-[var(--accent)]'}`}>
                {positive ? <TrendingUp size={11} className="inline mr-0.5" /> : <TrendingDown size={11} className="inline mr-0.5" />}
                {s.inverse ? '-' : '+'}{Math.abs(delta).toFixed(delta % 1 === 0 ? 0 : 1)} {s.unit} · {deltaPct.toFixed(1)}%
              </span>
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <div className="flex justify-between text-xs font-mono text-[var(--text-mute)] mb-1">
                  <span>STOCK</span><span>{s.stock} {s.unit}</span>
                </div>
                <div className="gauge-bar">
                  <div className="gauge-fill grow-bar" style={{ background: '#c7c7cc', width: `${stockPct}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs font-mono mb-1">
                  <span className="text-[var(--accent)] font-semibold">BUILD</span>
                  <span className="text-[var(--ink)] font-semibold">{s.built} {s.unit}</span>
                </div>
                <div className="gauge-bar">
                  <div className="gauge-fill grow-bar" style={{ background: 'linear-gradient(90deg, var(--accent), #ff7a28)', width: `${builtPct}%`, animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>

    <div className="card p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h3 className="font-display text-xl font-bold text-[var(--ink)]">Driving feel vs stock</h3>
        <div className="flex items-center gap-4 text-xs font-mono">
          <span className="flex items-center gap-1.5 text-[var(--text-dim)]"><span className="w-3 h-3 rounded-sm bg-[#c7c7cc]"></span> Stock</span>
          <span className="flex items-center gap-1.5 text-[var(--text-dim)]"><span className="w-3 h-3 rounded-sm bg-gradient-to-r from-[var(--accent)] to-[#ff7a28]"></span> Build</span>
        </div>
      </div>
      <div className="space-y-4">
        {specs.driving.map((d) => (
          <div key={d.label} className="grid grid-cols-[140px_1fr_70px] items-center gap-4">
            <span className="text-sm text-[var(--text-dim)]">{d.label}</span>
            <div className="relative h-3 rounded-full bg-[var(--bg-subtle)] overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-[#c7c7cc] rounded-full" style={{ width: `${d.stock}%` }} />
              <div className="absolute inset-y-0 left-0 grow-bar bg-gradient-to-r from-[var(--accent)] to-[#ff7a28] rounded-full" style={{ width: `${d.built}%`, animationDelay: '400ms' }} />
            </div>
            <span className="font-mono text-xs text-right">
              <span className="text-[var(--ink)] font-semibold">{d.built}</span>
              <span className="text-[var(--text-mute)]"> / {d.stock}</span>
            </span>
          </div>
        ))}
      </div>
    </div>

    <div className="grid md:grid-cols-2 gap-4">
      <div className="card p-6">
        <div className="section-label mb-5"><Cog size={13}/> Vehicle specs</div>
        <div className="space-y-3">
          {Object.entries(specs.vehicle).map(([k, v]) => (
            <div key={k} className="flex justify-between items-center pb-3 border-b border-[var(--border)] last:border-0">
              <span className="text-sm text-[var(--text-dim)]">{k}</span>
              <span className="font-mono text-sm text-[var(--ink)]">{v}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="card p-6">
        <div className="section-label mb-5"><Target size={13}/> Setup specs</div>
        <div className="space-y-3">
          {Object.entries(specs.setup).map(([k, v]) => (
            <div key={k} className="flex justify-between items-center pb-3 border-b border-[var(--border)] last:border-0 gap-4">
              <span className="text-sm text-[var(--text-dim)] flex-shrink-0">{k}</span>
              <span className="font-mono text-xs text-[var(--ink)] text-right">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ==================== TAGGED PRODUCTS ====================
// Layout:
//  Left  = multi-view media gallery with black "+" hotspots, arrows to
//          switch views, and a fullscreen/expand toggle.
//  Right = two modes:
//            (A) LIST  — tagged products grouped by system (01 Wheels, 02 Aero…)
//            (B) DETAIL — on tag/list-item click: product info (manufacturer
//                notes) + "XX Listings" of alternate products to buy.
const TaggedTab = () => {
  const gallery = build.taggedGallery;
  const [viewIdx, setViewIdx] = useState(0);
  const [activePartId, setActivePartId] = useState(null); // null = list mode
  const [hoveredHot, setHoveredHot] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const view = gallery[viewIdx];
  const nextView = () => setViewIdx((i) => (i + 1) % gallery.length);
  const prevView = () => setViewIdx((i) => (i - 1 + gallery.length) % gallery.length);

  // Close expanded view with ESC
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded, setExpanded]);

  // Per-view: parts visible in the CURRENT view only, grouped by system.
  // Group order = order in which their lowest-numbered hotspot appears in
  // the view, so the right-panel reads 01 → 02 → 03 top-to-bottom.
  const taggedBySystem = useMemo(() => {
    const seen = new Map(); // partId -> { part, firstIdx }
    view.hotspots.forEach((h, idx) => {
      if (!seen.has(h.partId)) {
        const p = parts.find((pp) => pp.id === h.partId);
        if (p) seen.set(h.partId, { part: p, firstIdx: idx });
      }
    });
    const bySys = {};
    for (const { part, firstIdx } of seen.values()) {
      if (!bySys[part.system]) bySys[part.system] = { parts: [], firstIdx };
      bySys[part.system].parts.push({ ...part, _idx: firstIdx });
      // keep the smallest firstIdx as the system's sort key
      if (firstIdx < bySys[part.system].firstIdx) {
        bySys[part.system].firstIdx = firstIdx;
      }
    }
    // Sort: groups by lowest firstIdx; parts within group by their _idx
    return Object.entries(bySys)
      .map(([sysId, { parts: ps, firstIdx }]) => ({
        system: systems.find((s) => s.id === sysId),
        parts: ps.sort((a, b) => a._idx - b._idx),
        firstIdx,
      }))
      .filter((g) => g.system)
      .sort((a, b) => a.firstIdx - b.firstIdx);
  }, [view]);

  // Per-view numbering — circles read 01, 02, 03… in the order hotspots
  // appear in this specific view's data.  Fresh per view (not global).
  const numberByPart = useMemo(() => {
    const m = {};
    let n = 1;
    view.hotspots.forEach((h) => {
      if (!m[h.partId]) {
        m[h.partId] = String(n).padStart(2, '0');
        n += 1;
      }
    });
    return m;
  }, [view]);

  return (
    <div className="mt-10" data-testid="tagged-tab">
      <div className={`grid gap-6 ${expanded ? 'lg:grid-cols-1' : 'lg:grid-cols-[1.5fr_1fr]'}`}>
        {/* ========== MEDIA COLUMN: viewer + thumbnail strip ========== */}
        <div className="space-y-3 min-w-0">
          <MediaViewer
            view={view}
            viewIdx={viewIdx}
            totalViews={gallery.length}
            hoveredHot={hoveredHot}
            setHoveredHot={setHoveredHot}
            activePartId={activePartId}
            setActivePartId={setActivePartId}
            expanded={expanded}
            setExpanded={setExpanded}
            onNext={nextView}
            onPrev={prevView}
            numberByPart={numberByPart}
          />

          {/* Thumbnail strip of gallery views (hidden in fullscreen mode) */}
          {!expanded && (
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x snap-mandatory scrollbar-thin">
              {gallery.map((v, i) => (
                <button
                  key={v.id}
                  onClick={() => setViewIdx(i)}
                  data-testid={`tagged-view-${v.id}`}
                  className={`w-[160px] aspect-[4/3] rounded-lg overflow-hidden border-2 transition relative flex-shrink-0 snap-start ${
                    i === viewIdx ? 'border-[var(--accent)] ring-2 ring-[var(--accent)]/20' : 'border-[var(--border)] hover:border-[var(--border-bright)]'
                  }`}
                >
                  <img src={v.image} alt={v.label} className="w-full h-full object-cover" />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-1.5">
                    <div className="text-[10px] font-display tracking-widest uppercase text-white flex items-center gap-1">
                      <MapPin size={9} /> {v.label}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ========== RIGHT PANEL ========== */}
        {!expanded && (
          <div className="min-w-0">
            {activePartId ? (
              <TagDetailPanel
                partId={activePartId}
                onBack={() => setActivePartId(null)}
                numberByPart={numberByPart}
              />
            ) : (
              <TagListPanel
                taggedBySystem={taggedBySystem}
                onSelect={(partId) => setActivePartId(partId)}
                hoveredHot={hoveredHot}
                numberByPart={numberByPart}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ========== Media viewer (image + hotspots + controls) ==========
const MediaViewer = ({
  view, viewIdx, totalViews, hoveredHot, setHoveredHot,
  activePartId, setActivePartId, expanded, setExpanded, onNext, onPrev,
  numberByPart,
}) => {
  const containerCls = expanded
    ? 'fixed inset-0 z-50 bg-black flex items-center justify-center p-6'
    : 'relative rounded-3xl overflow-hidden border border-[var(--border)] bg-[var(--bg-subtle)] aspect-[3/2]';

  const imgCls = expanded ? 'max-w-full max-h-full object-contain' : 'w-full h-full object-cover';

  return (
    <div className={containerCls} data-testid="tagged-media-viewer">
      <div className={expanded ? 'relative w-full h-full flex items-center justify-center' : 'contents'}>
        <img src={view.image} alt={view.label} className={imgCls} />

        {/* Hotspots */}
        {view.hotspots.map((h) => {
          const p = parts.find((pp) => pp.id === h.partId);
          if (!p) return null;
          const isActive = activePartId === h.partId;
          const isHovered = hoveredHot === h.id;
          const num = numberByPart?.[h.partId];
          return (
            <button
              key={h.id}
              data-testid={`tagged-hotspot-${h.id}`}
              onMouseEnter={() => setHoveredHot(h.id)}
              onMouseLeave={() => setHoveredHot(null)}
              onClick={() => setActivePartId(h.partId)}
              className={`hotspot ${isActive ? 'active' : 'pulse-ring'}`}
              style={{ left: `${h.x}%`, top: `${h.y}%`, transform: 'translate(-50%, -50%)' }}
              aria-label={`${p.brand} ${p.name}`}
            >
              <span className="font-display font-bold text-[13px] leading-none">{num || '+'}</span>
              {/* Hover preview card */}
              {isHovered && (
                <div
                  className="hotspot-tooltip"
                  style={{
                    left: h.x > 65 ? 'auto' : '48px',
                    right: h.x > 65 ? '48px' : 'auto',
                    top: h.y > 65 ? 'auto' : '-8px',
                    bottom: h.y > 65 ? '-8px' : 'auto',
                  }}
                >
                  <div className="text-[10px] font-display tracking-widest uppercase text-[var(--text-mute)]">{p.brand}</div>
                  <div className="font-semibold text-sm text-[var(--ink)] mt-0.5 leading-tight">{p.name}</div>
                  <div className="mt-3 aspect-square rounded-lg overflow-hidden bg-[var(--bg-subtle)]">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="font-mono text-sm font-semibold text-[var(--accent)]">${fmt(p.priceNum)}</span>
                    <span className="text-xs text-[var(--text-dim)] underline underline-offset-2">View Details</span>
                  </div>
                </div>
              )}
            </button>
          );
        })}

        {/* Top-left: view label */}
        <div className="absolute top-4 left-4 chip bg-white/95 backdrop-blur shadow">
          <MapPin size={11} /> {view.label} · <span className="font-mono">{viewIdx + 1}/{totalViews}</span>
        </div>

        {/* Top-right: info icon */}
        <button
          className="absolute top-4 right-4 w-9 h-9 rounded-lg bg-black/70 hover:bg-black text-white flex items-center justify-center transition backdrop-blur"
          aria-label="Information"
        >
          <Info size={16} />
        </button>

        {/* Left arrow */}
        {totalViews > 1 && (
          <button
            onClick={onPrev}
            data-testid="tagged-view-prev"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/95 hover:bg-white shadow-lg flex items-center justify-center transition text-[var(--ink)]"
            aria-label="Previous view"
          >
            <ChevronLeft size={18} />
          </button>
        )}

        {/* Right arrow */}
        {totalViews > 1 && (
          <button
            onClick={onNext}
            data-testid="tagged-view-next"
            className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/95 hover:bg-white shadow-lg flex items-center justify-center transition text-[var(--ink)]"
            aria-label="Next view"
          >
            <ChevronRight size={18} />
          </button>
        )}

        {/* Bottom-left: hotspots-count chip */}
        <div className="absolute bottom-4 left-4 chip bg-white/95 backdrop-blur shadow">
          <Tag size={11} /> {view.hotspots.length} tagged
        </div>

        {/* Bottom-right: expand / close */}
        <button
          onClick={() => setExpanded(!expanded)}
          data-testid="tagged-expand-btn"
          className="absolute bottom-4 right-4 w-10 h-10 rounded-lg bg-black/80 hover:bg-black text-white flex items-center justify-center transition backdrop-blur"
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? <X size={18} /> : <Maximize2 size={16} />}
        </button>
      </div>
    </div>
  );
};

// ========== List panel: tagged products grouped by system ==========
const TagListPanel = ({ taggedBySystem, onSelect, numberByPart }) => (
  <div className="space-y-5">
    <div>
      <div className="section-label mb-1"><Tag size={13} /> Tagged Products</div>
      <p className="text-sm text-[var(--text-dim)]">
        {taggedBySystem.reduce((sum, g) => sum + g.parts.length, 0)} parts tagged across {taggedBySystem.length} systems. Click any tag to see info + listings.
      </p>
    </div>

    <div className="space-y-5 pr-1 max-h-[640px] overflow-y-auto">
      {taggedBySystem.map((group) => {
        const SIcon = iconMap[group.system.icon];
        return (
          <div key={group.system.id} data-testid={`tagged-group-${group.system.id}`}>
            <div className="flex items-center gap-2 mb-2 sticky top-0 bg-white py-1 z-10">
              <SIcon size={14} className="text-[var(--accent)]" />
              <span className="font-display text-xs tracking-widest uppercase text-[var(--ink)] font-semibold">
                {group.system.name}
              </span>
              <span className="font-mono text-[10px] text-[var(--text-mute)]">· {group.parts.length}</span>
              <div className="flex-1 h-px bg-[var(--border)] ml-2" />
            </div>

            <div className="space-y-2">
              {group.parts.map((p) => (
                <button
                  key={p.id}
                  onClick={() => onSelect(p.id)}
                  data-testid={`tagged-list-${p.id}`}
                  className="w-full card card-hover p-4 flex items-center gap-4 text-left transition"
                >
                  <div className="w-11 h-11 rounded-full bg-black flex items-center justify-center font-display font-bold text-sm text-white flex-shrink-0">
                    {numberByPart?.[p.id] || '—'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-display tracking-widest uppercase text-[var(--text-mute)] mb-0.5">
                      {group.system.name}
                    </div>
                    <div className="font-display text-base font-semibold text-[var(--ink)] truncate leading-tight">
                      {p.name}
                    </div>
                    <div className="text-[10px] font-display tracking-widest uppercase text-[var(--text-mute)] mt-1 flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-[var(--accent)]" />
                      {p.brand}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-[11px] text-[var(--text-dim)] font-mono">
                      tap for info &amp; listings
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// ========== Detail panel: single tagged product ==========
const TagDetailPanel = ({ partId, onBack, numberByPart }) => {
  const [heroIdx, setHeroIdx] = useState(0);
  const listings = getListingsForPart(partId);
  const part = listings.current;

  // Reset carousel selection when switching between tagged products
  useEffect(() => { setHeroIdx(0); }, [partId, setHeroIdx]);

  if (!part) return null;

  const sys = systems.find((s) => s.id === part.system);
  const SIcon = iconMap[sys?.icon];

  // Same global number used on the image hotspot pin
  const num = numberByPart?.[partId] || '—';

  // Mini image carousel — the part + its alternates' thumbnails
  const carouselImages = [part.image, ...listings.alternates.map((a) => a.image)].slice(0, 4);
  const hero = carouselImages[heroIdx] || part.image;

  return (
    <div className="space-y-5 fade-up" data-testid={`tagged-detail-${partId}`}>
      {/* Back link */}
      <button
        onClick={onBack}
        data-testid="tagged-back-btn"
        className="flex items-center gap-2 text-sm text-[var(--text-dim)] hover:text-[var(--ink)] transition"
      >
        <ChevronLeft size={15} /> Back to tags
      </button>

      {/* Title row */}
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-full bg-black flex items-center justify-center font-display font-bold text-white">
          {num}
        </div>
        <div>
          <div className="flex items-center gap-2">
            {SIcon && <SIcon size={14} className="text-[var(--accent)]" />}
            <span className="text-[10px] font-display tracking-widest uppercase text-[var(--text-mute)]">{sys?.name}</span>
          </div>
          <h3 className="font-display text-2xl font-bold text-[var(--ink)] leading-tight">{part.name}</h3>
        </div>
      </div>

      {/* Carousel: big hero + thumbnail strip */}
      <div className="card overflow-hidden">
        <div className="aspect-[4/3] bg-[var(--bg-subtle)] relative">
          <img src={hero} alt={part.name} className="w-full h-full object-cover" />
          <span className="absolute top-3 right-3 chip bg-white/95 backdrop-blur">
            <Star size={11} className="text-[var(--star)]" fill="currentColor" /> {part.rating}
          </span>
        </div>
        {carouselImages.length > 1 && (
          <div className="flex items-center gap-2 p-3 border-t border-[var(--border)]">
            {carouselImages.map((src, i) => (
              <button
                key={src}
                onClick={() => setHeroIdx(i)}
                className={`w-14 h-14 rounded-lg overflow-hidden border-2 flex-shrink-0 transition ${
                  heroIdx === i ? 'border-[var(--accent)]' : 'border-[var(--border)] hover:border-[var(--border-bright)]'
                }`}
                data-testid={`tagged-thumb-${i}`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Product info — manufacturer-level marketing copy */}
      <div className="space-y-3">
        <div className="section-label"><Info size={13} /> Product info</div>
        <ProductInfoText text={part.manufacturerInfo || part.whyThisPart || part.note} />
        <div className="p-4 rounded-lg bg-[var(--amber-soft)] border border-[var(--amber)]/25">
          <div className="flex items-center gap-1.5 text-[10px] font-display tracking-widest uppercase text-[var(--amber)] mb-1.5">
            <Trophy size={11} /> Stan&apos;s note · what&apos;s on the car
          </div>
          <p className="leading-relaxed text-[var(--text-dim)] text-sm">{part.note}</p>
        </div>
      </div>

      {/* Listings */}
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <h4 className="font-display text-2xl font-bold text-[var(--ink)]">{listings.total} Listings</h4>
          <span className="text-xs text-[var(--text-mute)] font-mono">Prices from partners</span>
        </div>

        <div className="space-y-3">
          {/* Current build listing — what Stan actually runs */}
          <ListingRow
            name={part.name}
            brand={part.brand}
            price={part.priceNum}
            image={part.image}
            badge="On this build"
          />
          {listings.alternates.map((a) => (
            <ListingRow
              key={`${a.brand}-${a.name}`}
              name={a.name}
              brand={a.brand}
              price={a.price}
              image={a.image}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Manufacturer "Product info" body — truncates long descriptions. "Read more"
// jumps to the Product PDP (Shop page) with the "Vehicle Details" tab active,
// where the full manufacturer detail + listings live.
const ProductInfoText = ({ text }) => {
  const navigate = useNavigate();
  if (!text) return null;
  const LIMIT = 180;
  const isLong = text.length > LIMIT;
  const visible = isLong ? text.slice(0, LIMIT).trimEnd() + '…' : text;
  return (
    <p
      className="text-[var(--text-dim)] leading-relaxed text-sm"
      data-testid="tagged-manufacturer-info"
    >
      {visible}
      {isLong && (
        <>
          {' '}
          <button
            onClick={() => navigate('/shop?tab=details')}
            data-testid="tagged-read-more"
            className="text-[var(--accent)] font-medium hover:underline underline-offset-2 inline-flex items-center gap-1"
          >
            Read more <ArrowRight size={11} />
          </button>
        </>
      )}
    </p>
  );
};

// Row component for a single listing inside the detail panel
const ListingRow = ({ name, brand, price, image, badge }) => (
  <div className="card card-hover p-3 flex items-center gap-4" data-testid="tagged-listing-row">
    <div className="w-20 h-20 rounded-lg overflow-hidden bg-[var(--bg-subtle)] flex-shrink-0 border border-[var(--border)]">
      <img src={image} alt={name} className="w-full h-full object-cover" />
    </div>
    <div className="flex-1 min-w-0">
      {badge && (
        <span className="chip bg-[var(--accent-soft)] text-[var(--accent)] border-[var(--accent)]/40 text-[10px] mb-1.5">
          <Check size={10} /> {badge}
        </span>
      )}
      <div className="text-[10px] font-display tracking-widest uppercase text-[var(--text-mute)]">{brand}</div>
      <div className="font-semibold text-sm text-[var(--ink)] truncate">{name}</div>
      <div className="font-mono text-base font-semibold text-[var(--ink)] mt-1">${fmt(price)}</div>
    </div>
    <button className="text-sm text-[var(--ink)] hover:text-[var(--accent)] underline underline-offset-2 flex-shrink-0 font-medium">
      View Details
    </button>
  </div>
);

// ==================== COMMENTS ====================
const CommentSection = () => (
  <section className="max-w-[1440px] mx-auto px-6 mt-20">
    <div className="flex items-center gap-3 mb-6">
      <h2 className="font-display text-2xl font-bold text-[var(--ink)]">Comments</h2>
      <span className="chip font-mono">{build.stats.comments}</span>
    </div>
    <div className="space-y-4">
      {comments.map((c) => (
        <div key={c.id} className="card p-5 flex gap-4">
          <img src={c.avatar} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-[var(--ink)]">{c.user}</span>
              <span className="text-xs text-[var(--text-mute)] font-mono">{c.time}</span>
            </div>
            <p className="mt-1.5 text-[var(--text-dim)] leading-relaxed">{c.body}</p>
            <div className="mt-3 flex items-center gap-4 text-xs text-[var(--text-dim)]">
              <button className="flex items-center gap-1.5 hover:text-[var(--accent)] transition"><Heart size={13}/> {c.likes}</button>
              <button className="hover:text-[var(--ink)] transition">Reply</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

// ==================== MORE BUILDS ====================
const MoreBuilds = () => (
  <section className="max-w-[1440px] mx-auto px-6 mt-20 mb-24">
    <div className="flex items-center justify-between mb-6">
      <h2 className="font-display text-2xl font-bold text-[var(--ink)]">More builds like this</h2>
      <button className="text-sm text-[var(--text-dim)] hover:text-[var(--ink)] flex items-center gap-1">See all <ArrowRight size={14}/></button>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {moreBuilds.map((b) => (
        <div key={b.id} className="card card-hover overflow-hidden group cursor-pointer">
          <div className="aspect-[4/3] overflow-hidden bg-[var(--bg-subtle)]">
            <img src={b.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
          </div>
          <div className="p-4">
            <div className="text-[10px] font-display tracking-widest uppercase text-[var(--text-mute)]">Build</div>
            <div className="font-semibold text-sm truncate text-[var(--ink)]">{b.title}</div>
            <div className="mt-1 flex items-center justify-between text-xs text-[var(--text-dim)]">
              <span>{b.owner}</span>
              <span className="flex items-center gap-1 font-mono"><Heart size={11}/> {fmt(b.likes)}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

// ==================== PAGE ====================
export default function BuildShowcase() {
  const [activeTab, setActiveTab] = useState('Overview');
  // When the user clicks a part inside the Breakdown tab, this carries
  // the partId across to the Parts tab so it can pre-select it.
  const [pendingPartId, setPendingPartId] = useState(null);

  const jumpToPart = (partId) => {
    setPendingPartId(partId);
    setActiveTab('Parts');
  };

  return (
    <div className="min-h-screen bg-white text-[var(--ink)] pt-16">
      <TopNav />
      <Hero />
      <TitleBlock />
      <KPIStrip />
      <TabBar active={activeTab} setActive={setActiveTab} />
      <main className="max-w-[1440px] mx-auto px-6 pb-10">
        {activeTab === 'Overview' && <OverviewTab onGoToBreakdown={() => setActiveTab('Breakdown')} />}
        {activeTab === 'Breakdown' && <BreakdownTab onJumpToPart={jumpToPart} />}
        {activeTab === 'Parts' && (
          <PartsTab
            initialPartId={pendingPartId}
            onConsumed={() => setPendingPartId(null)}
          />
        )}
        {activeTab === 'Specs' && <SpecsTab />}
        {activeTab === 'Tagged Products' && <TaggedTab />}
      </main>
      <CommentSection />
      <MoreBuilds />
      <footer className="border-t border-[var(--border)] py-8 bg-[var(--bg-subtle)]">
        <div className="max-w-[1440px] mx-auto px-6 flex items-center justify-between flex-wrap gap-4 text-xs text-[var(--text-mute)] font-mono">
          <div className="flex items-center gap-4">
            <img src="/brand/berides-logo.png" alt="beRides" className="h-6 w-auto opacity-80" />
            <span>© 2026 beRides · Build showcase</span>
          </div>
          <span>Real builds. What actually works. Built by enthusiasts.</span>
        </div>
      </footer>
    </div>
  );
}
